import {
  isFeatureAvailableForProject,
  isFeatureAvailableForWorkspace,
} from '@/utils/projects'

import {
  Project,
  ProjectFeatures,
  Workspace,
  WorkspaceDescriptor,
} from '@/types/index.d'

export const PROTECTED_WORKSPACE_ROUTES = [
  {
    routeRegExp: /^\/insights\/people$/,
    feature: 'people_insights',
    roles: ['ADMIN'],
  },
  {
    routeRegExp: /^\/insights\/security$/,
    feature: 'security_insights',
    roles: ['ADMIN'],
  },
  {
    routeRegExp: /^\/insights\/impact$/,
    feature: 'impact_report',
    roles: ['ADMIN'],
  },
  {
    routeRegExp: /^\/user\/settings$/,
    roles: ['ADMIN', 'USER', 'COLLABORATOR'],
  },
  {
    routeRegExp: /^\/settings.*/,
    roles: ['ADMIN'],
  },
  { routeRegExp: /^\/store$/, roles: ['ADMIN'] },
] as ProtectedWorkspaceRouteRule[]

export const PROTECTED_PROJECT_ROUTES = [
  {
    routeRegExp: /^\/repositories\/.*\/settings.*/,
    roles: ['ADMIN', 'LEAD'],
  },
  {
    routeRegExp: /repositories\/.*\/hardening$/,
    feature: 'hardening_report',
    roles: ['ADMIN', 'LEAD'],
  },
] as ProtectedProjectRouteRule[]

type ProtectedProjectRouteRule = {
  routeRegExp: RegExp
  feature?: keyof ProjectFeatures
  roles: ['ADMIN' | 'COLLABORATOR' | 'USER', 'LEAD' | 'DEVELOPER']
}

type ProtectedWorkspaceRouteRule = {
  routeRegExp: RegExp
  feature: keyof WorkspaceDescriptor
  roles: ['ADMIN' | 'COLLABORATOR' | 'USER']
}

export function isUnauthorizedWorkspaceAccess({
  accountRole,
  pathname,
  workspace,
}: {
  accountRole: 'ADMIN' | 'USER' | 'COLLABORATOR'
  pathname: string
  workspace: Workspace
}): boolean {
  const protectedRoute = PROTECTED_WORKSPACE_ROUTES.filter((route) =>
    pathname.match(route.routeRegExp)
  )[0]

  const requiredAccountRoles = protectedRoute?.roles
  const requiredFeature = protectedRoute?.feature

  if (!protectedRoute) {
    return false
  }

  if (
    // Unauthorized if the feature is not available for this workspace
    typeof requiredFeature !== 'undefined' &&
    !isFeatureAvailableForWorkspace(requiredFeature, workspace)
  ) {
    return true
  }

  if (requiredAccountRoles.indexOf(accountRole) < 0) {
    return true
  }

  return false
}

export function isUnauthorizedProjectAccess({
  projectRole,
  accountRole,
  pathname,
  project,
  isLoading,
}: {
  projectRole: 'LEAD' | 'DEVELOPER'
  accountRole: 'ADMIN' | 'USER' | 'COLLABORATOR'
  pathname: string
  project: Project
  isLoading?: boolean
}) {
  const protectedRoute = PROTECTED_PROJECT_ROUTES.filter((route) =>
    pathname.match(route.routeRegExp)
  )[0]
  const requiredRoles = protectedRoute?.roles

  if (!protectedRoute) {
    return false
  }

  if (
    // Unauthorized if the feature is not available for this project
    !isLoading &&
    typeof protectedRoute?.feature !== 'undefined' &&
    !isFeatureAvailableForProject(protectedRoute?.feature, project)
  ) {
    return true
  }

  if (
    // Authorized if the feature is available but the user is neither a workspace admin nor a project lead but Unauthorized if its not applying the three conditions
    !isLoading &&
    !requiredRoles.includes(accountRole) &&
    !requiredRoles.includes(projectRole) &&
    !isFeatureAvailableForProject(protectedRoute?.feature, project)
  ) {
    return true
  }

  if (
    !isLoading &&
    !requiredRoles.includes(accountRole) &&
    requiredRoles.includes(projectRole)
  ) {
    return false
  }

  return false
}
